import { useState, type FormEvent } from 'react';
import { MessageSquarePlus, AlertCircle } from 'lucide-react';
import type { Station } from '@hcc/types';
import { Button } from '../ui/Button';

/**
 * Free-text note attached to a person. Notes are persistent context
 * (accessibility, preferences) — distinct from flags which are
 * things to chase. When opened from a station view, callers pass the
 * station so the note records who to ask about it later.
 */
export function AddNoteModal({
  open,
  personName,
  station,
  onClose,
  onSubmit,
}: {
  open: boolean;
  personName: string;
  /** Auto-tags the note with the station it was added from. */
  station?: Station;
  onClose: () => void;
  onSubmit: (input: { text: string; station?: Station }) => void | Promise<void>;
}) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) {
      setError('Please write a note');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        text: text.trim(),
        ...(station ? { station } : {}),
      });
      setText('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to add note');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-note-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg"
      >
        <header className="flex items-center gap-3 border-b border-divider/10 bg-info-soft/15 px-5 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-token-sm bg-info/15 text-info">
            <MessageSquarePlus className="h-5 w-5" />
          </div>
          <div>
            <h2 id="add-note-title" className="text-base font-semibold tracking-tight text-fg">
              Add a note
            </h2>
            <p className="text-xs text-fg-muted">
              for <strong>{personName}</strong>
              {station ? <> · from <strong>{station}</strong></> : null}
            </p>
          </div>
        </header>

        <div className="space-y-4 p-5 sm:p-6">
          <p className="text-xs text-fg-muted">
            Notes are persistent context that every desk can see. Use for
            accessibility needs, preferred name, anything other staff
            should know. Not a flag — notes aren't expected to be cleared.
          </p>

          <label className="block text-sm">
            <span className="font-medium text-fg">Note</span>
            <textarea
              required
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Dyslexic — please use a larger font on the tablet"
              className="mt-1.5 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </label>

          {error ? (
            <div className="flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-5 py-3">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="info"
            size="md"
            iconLeft={<MessageSquarePlus className="h-4 w-4" />}
            loading={submitting}
          >
            Add note
          </Button>
        </footer>
      </form>
    </div>
  );
}
